/**
 * One phase row on the run's pipeline spine — Extract, Plan, or Send, stacked by a caller into the
 * three-row stage. See `lib/run-phases.ts` for what a `RunPhase` means and where its numbers come from.
 *
 * NO PHASE LABELS ON SCREEN. Phase is carried by three things only: the owning party's brand mark
 * (`PhaseMark`), vertical order along the spine (the caller's stacking order, via `isFirst`/`isLast`),
 * and the structurally distinct SHAPE of the breakdown line (`nulls · duplicates` / `new · updated ·
 * deleted` / `failed · dropped` read as different things at a glance, without a caption). The phase
 * name still has to exist somewhere for assistive tech — the marks are `aria-hidden`, so an unaided
 * screen-reader pass would otherwise hear three unlabelled numbers. `ActivationStrip` solves the exact
 * same problem the exact same way (an `sr-only` role word beside a decorative glyph); this is that
 * pattern applied to a pipeline row instead of an endpoint.
 *
 * THE SPINE SPANS THE BLOCK'S FULL HEIGHT (owner decision, 2026-10-30: "full width or full height
 * depending on the orientation" — the same rule as `RunStrip`'s track across). Extract's mark sits at
 * the top of the three-row block, Send's at the bottom and Plan's in the exact centre, and the two legs
 * stretch to fill the space between them. Each row is a two-column grid: the RAIL (the incoming leg
 * half, the plate, the outgoing leg half — one flex column spanning every band) and the text, whose
 * bands are the upper leg band (`1fr`), the MARK band (`auto`, the stats) and the lower leg band (`1fr`,
 * where the working leg's label goes). The caller stacks the rows in `PhaseStack`, whose rows are all
 * the height of the tallest. Equal rows and equal `1fr` bands are what centre Plan; the stretched legs
 * are what put the first and last marks at the block's edges. Each row's text is aligned to its plate
 * the same way — from the top, centred, to the bottom. The height is the text's own: nothing is sized
 * to the viewport. The 13px floor on a leg band (`LEG_BAND`) keeps a leg from ever vanishing.
 *
 * A LEG IS STILL DRAWN AS TWO HALVES, one per row (`spine-out` of row N, `spine-in` of row N+1), and the
 * rows are stacked with no gap so the halves touch. `spine-in` is omitted on the first row and
 * `spine-out` on the last, so the line starts and stops at the first and last mark. Both halves are the
 * shared `PhaseLeg` piece (`slot="spine-in"`/`"spine-out"`), round-capped on the leg's two outer ends only.
 *
 * RUN STATE LIVES ON THE LEGS — the EDGES of the spine, not its nodes. There are no phase dots any
 * more: `DOT_CLASSES` and the `phase-dot` span are deleted, and with them the claim that the payload
 * can place a run at three points. It cannot. `activePhase()` returns only `extract` or `send` (it
 * has no `plan` arm at all), so the wire distinguishes exactly TWO live positions — which is exactly
 * how many edges three marks have. Leg 1 spans Extract->Plan, leg 2 spans Plan->Send, and each wears
 * one of four treatments (see `LEG_TREATMENT`). The plate stays recessive (`PhaseMark`) and carries
 * nothing; it is the one thing about the old model that survives unchanged.
 *
 * A LEG IS SHARED BETWEEN TWO ROWS and each row draws only half of it: leg N is row N's `spine-out`
 * plus row N+1's `spine-in`, rendered by two `PhaseStage`s that never see each other. The two halves
 * agree because they are the SAME VALUE — `deriveRunPhases` computes both leg states once
 * (`legStates()`, `run-phases.ts`) and hands each to the two rows that draw a piece of it. Nothing
 * here re-derives a leg's state from the row's own activity; doing so is how a leg becomes two
 * colours.
 *
 * The rule constrains PIXELS, not the accessible tree. The connector's `title` and the row's
 * `sr-only` activity word (see `PHASE_ACTIVITY_LABEL`) paint nothing and are not exceptions to it.
 *
 * NO PROSE, NO METER, NO ANIMATION BETWEEN READINGS, AND NO BAR ON A PHASE ROW. A leg may say WHERE
 * the run is and, while it is working, WHAT is happening in the reader's own words; it may not say
 * how far along or how long is left. That is the ban, and it is narrower than the one this comment
 * used to state. The working leg's pulse is a state indicator with no reading in it — it does not
 * move with the numbers and does not encode a fraction — which is why it is not the animation this
 * bans.
 *
 * THE BAR CLAUSE IS NOW SCOPED, NOT ABSOLUTE. This comment used to read "NO PROGRESS BAR" flat, and
 * that is superseded by the canonical spec, Figma `500:1295`, whose `State=Sending` variant draws one:
 * a `rule` plus a `Delivery` frame BELOW the three phase rows, inside the same card. It is built by
 * `DeliveryProgress.tsx` and composed by `RunOverview`, and the two halves of the rule are:
 *
 *   PERMITTED, in that one slot — a CONDITIONAL, DISCRETE, NEVER-ANIMATED track, rendered only while
 *     the run is live AND `changed.total > 10,000` (over one `gateway.maxBatchSizeEvents` batch) AND
 *     the run has outlived one ~30s `reporting.asyncReporterInterval` flush. `changed.total === -1` is
 *     the count-query-timeout sentinel and gets no bar ever. Figma annotation `281:289` is the source;
 *     it says in as many words that "the no-bar case is the default, not the exception".
 *   STILL BANNED, here and everywhere — an UNCONDITIONAL bar, a bar that fills continuously, and any
 *     interpolation, easing or shimmer between two readings. That is what `LiveRunProgress` was and
 *     why it was deleted (`5b1044a2`); `DeliveryProgress` is not a restoration of it.
 *
 * A `PhaseStage` row itself still renders no bar under any payload, which is what `PhaseStage.test.tsx`
 * pins — the permitted bar is the CARD's, one level up, and a row that grew its own would be back to
 * three meters stacked on a spine.
 *
 * EVERY ROW NAMES BOTH ITS NUMBERS. `RunDenominated` takes a word for the numerator as well as one
 * for the denominator, and each row's numerator word IS the next row's denominator word:
 *
 *   84,120 valid    of 85,324 returned · 98.6%
 *    1,230 changed  of 84,120 valid    · 1.5%
 *      162 accepted of  1,230 changed  · 13.2%
 *
 * Reading the first noun down the stack spells the pipeline — valid -> changed -> accepted — which is
 * the job the deleted phase captions used to do. Before this, `unit` named the denominator alone and
 * each row's own headline number was the one quantity on the row with no name at all.
 *
 * IT IS NOT A BAN ON PERCENTAGES. A live Send row renders `162,380 accepted of 400,000 changed · 40.6%` and
 * that is CORRECT — it is Band B's own Send/Live variant (and `liveSendingMidFlight`, an OBSERVED
 * fixture, so `PhaseStage.test.tsx` pins that exact string). The percentage comes out of
 * `RunDenominated`/`denominated()`, which every number on this surface routes through so a count can
 * never appear without its denominator. A percentage of a stated denominator is a READING; a bar is a
 * PREDICTION, and the difference is the whole rule. The earlier wording ("NO PERCENTAGE OF
 * COMPLETION") described a component that does not exist, and the next reader to trust it would have
 * deleted working, designed behaviour as a regression. Do not restore it.
 *
 * Every visible string is a number+denominator (`RunDenominated`), the platform's own `state` string
 * verbatim, or one of the two working-leg labels — never a free sentence this component composes
 * about the numbers. An idle phase renders no `RunDenominated` at all: `n`/`d` are real numbers even
 * when idle (see `run-phases.ts`), but a phase that has not started has nothing to measure, and
 * "0 of 0 returned" would assert a result nobody has looked for.
 *
 * AN IDLE PHASE IS ONE OF TWO THINGS, and they get opposite treatments. A bare em dash used to serve
 * both, which flattened them:
 *
 *   PENDING (`idleKind: 'pending'`) — the run is live and this phase is genuinely coming. A
 *     `Skeleton` goes here: a shape reserves the space a number will occupy without asserting a
 *     value, which is the same thing this app's skeletons mean everywhere else.
 *   VOID (`idleKind: 'void'`) — the run already stopped. This phase never happened and never will, so
 *     nothing renders. A skeleton would promise a number that is not coming, which is worse than the
 *     em dash it replaced rather than better.
 *
 * Neither shape reaches a screen reader, so the DIFFERENCE lives in the row's `sr-only` word: pending
 * announces "Not started", void announces {@link IDLE_VOID_LABEL}.
 *
 * A row is therefore at most three lines and often two. It briefly carried a fourth: a `metrics.*`
 * PROVENANCE FOOTER (`metrics.changed.total` and friends) right-aligned opposite the state string.
 * That was cut — a payload field path is not something a customer can act on, and Extract's was not
 * even a field name but an arithmetic expression. The field each number comes from is still written
 * down, next to where the number is built in `run-phases.ts`. Do not put it back on screen.
 *
 * PIXEL FIGURES IN THIS FILE'S PROSE ARE FINE, and the ones that survive (352px at the JSX root, the
 * 46px mark band and 13px leg floor, the 28px connector column) each sit beside the code they constrain. This file used
 * to cite "CLAUDE.md's 'do not quote a pixel measurement' note" as though prose figures were banned.
 * There is no such note: ui/AGENTS.md bans hardcoded px in Tailwind classes and inline styles, and says
 * nothing about prose. The nearest real text is in `.claude/skills/ui/SKILL.md`, which says "Do not
 * quote a pixel measurement IN THIS FILE" — scoped to that document, after two figures in it turned
 * out to be wrong. Its instruction to everyone else is the opposite of a ban: if you need a figure,
 * measure it at 1280x800 with the sidebar expanded, put it next to the code it constrains, and say
 * where it came from. Do not re-add the citation.
 */
import { Fragment, useLayoutEffect, useRef } from 'react';

import { Skeleton } from '@/_shared/components/ui/skeleton';
import { cn } from '@/_shared/lib/utils';
import { destinationTypeIcon } from '@/audiences/icons/destinationTypes';

import type { LegState, PhaseActivity, RunPhase } from '../lib/run-phases';
import { PHASE_LABEL } from '../lib/run-phases';
import { PhaseMark } from './PhaseMark';
import { RunDenominated } from './RunDenominated';

/**
 * The four edge treatments. SHAPE, not hue, is what carries the state — all four differ in geometry
 * before they differ in colour, which is WCAG 1.4.1 and is met visually or not at all:
 *
 *   working    3px, brand blue, breathing   the run is on this segment right now
 *   done       2px, muted-foreground        it got across
 *   failed     3px, danger                  it stopped here
 *   unreached  2px DASHED, border           it never started, and never will on this run
 *
 * ROUND CAPS. Each leg ends in a round cap where it meets a plate (`LEG_SHAPE`), on the spine
 * and on `RunStrip`'s track alike. The rounding needs a line at least 2px thick to show, which is why
 * `unreached` went from 1px to 2px: it still differs from `done` by its DASH, which is the shape this
 * table relies on.
 *
 * THE `done` LEG WAS 1px `border-border`, AND MAKING IT HEAVY IS A REVERT OF A REVERT. Read this
 * before changing it back: a prominent connector has already been tried once, reverted, and the
 * reasoning for that revert was itself deleted by the rewrite that made it obsolete.
 *
 *   45a213da introduced the row with "the connector is a neutral 1px `border` hairline and stays that
 *     colour in every activity. It was briefly a 2px success-green line, which made a settled run look
 *     like a wiring diagram and competed with the numbers for attention; reverted."
 *   6342515c ("run state moves from the phase dots to the spine's two legs") deleted that sentence
 *     with the whole `RUN STATE LIVES ON THE DOT ALONE` paragraph it lived in. Nothing recorded the
 *     trade-off afterwards, so the next reader had no way to know a heavier connector had been judged.
 *
 * That judgement does NOT survive the rewrite, which is why the leg is heavy again. It was made
 * against a card where the DOTS carried run state and the connector carried nothing — there, a
 * decorative rule competing with the numbers for attention is straightforwardly bad. The edge model
 * inverts exactly that premise: the connector IS the state now, and there is no dot left to read it
 * from. The measurement that forces the change: `border-border` (`#ebeef1`) against the card's own
 * `#ffffff` is **1.23:1**, below every legibility floor there is — the completed spine was not
 * subtle, it was absent. The spec (Figma `500:1295`) binds this leg to `semantic/muted-foreground` at
 * 2px, and that is what ships.
 *
 * It is also NOT the old green. The reverted attempt coloured the leg by OUTCOME (success), which is
 * what made a finished run read as a wiring diagram; `muted-foreground` is the body-secondary grey,
 * so the leg recedes behind the 20px headline numbers instead of competing with them. If a future
 * reader still judges it too loud, the middle path is `border-border` at 2px — say so, do not go back
 * to 1px, and do not delete this paragraph when you change it.
 *
 * `unreached` is dashed and not merely paler. "Never started" and "finished" are different facts and
 * a stopped run must not draw the segments below the failure as a slightly lighter version of the
 * ones above it.
 *
 * `primary`, NOT `info`. `info` looks like the obvious token for "in flight" — `SyncStatusBadge` uses
 * it for a running run — but in this app `--info` resolves to `--grey-500` (`#6c7688`), a GREY. A grey
 * working leg is indistinguishable from a done one at 1px difference. `--primary` is `--blue-500`
 * (`#105ed5`), the brand blue, and both flip correctly in dark. Verified in `app.css` before choosing.
 *
 * REDUCED MOTION. The pulse is decoration and the state survives without it, twice over:
 *  1. The working leg — and only the working leg — carries a LABEL in the reader's own words
 *     ("Preparing your audience" / "Sending to Meta"). That is text: it works with no motion, in
 *     greyscale, in print, and for a screen reader. It, not the thickness, is what separates working
 *     from `failed`, which is the pair that matters (both are 3px and differ otherwise only in hue).
 *  2. For a reader scanning the spine rather than the labels, `motion-reduce` swaps the 3px solid rule
 *     for a 4px DOUBLE rule — two hairlines with a gap, a distinct geometry rather than a distinct
 *     colour or a distinct weight. It sits half a pixel further right of the column axis than the 3px
 *     rule does, which is the same order of sub-pixel drift the odd/even weights on this rail already
 *     have and is not worth a negative-margin correction to chase.
 *
 * The keyframes are the app's own `--animate-activity-pulse` (`app.css`, opacity 1 -> 0.5 over 2s),
 * not a fresh local animation.
 */
const LEG_TREATMENT: Record<LegState, string> = {
  working: 'border-primary motion-safe:animate-activity-pulse motion-reduce:border-double',
  done: 'border-muted-foreground',
  failed: 'border-danger',
  unreached: 'border-dashed border-border',
};

/**
 * Each state's line WEIGHT, per axis — the only thing that differs between the spine's legs (`down`,
 * a left border) and `RunStrip`'s track legs (`across`, a top border). The values are the same
 * weights; they are written once per axis only because Tailwind cannot see a class built by string
 * replacement. Colour, dash, pulse and the reduced-motion double rule are {@link LEG_TREATMENT}, shared.
 */
const LEG_WEIGHT: Record<'down' | 'across', Record<LegState, string>> = {
  down: {
    working: 'border-l-[0.1875rem] motion-reduce:border-l-4',
    done: 'border-l-2',
    failed: 'border-l-[0.1875rem]',
    unreached: 'border-l-2',
  },
  across: {
    working: 'border-t-[0.1875rem] motion-reduce:border-t-4',
    done: 'border-t-2',
    failed: 'border-t-[0.1875rem]',
    unreached: 'border-t-2',
  },
};

/**
 * A phase's mark as a standalone node — `RunStrip`'s track, which draws the marks on their own row
 * above the text. The same `PhaseMark` the rows draw, with the same `title` (the activity word) the
 * vertical connector carries for the mouse; decorative, like the rows' own.
 */
export function PhaseNode({
  phase,
  warehouseName,
  destinationDefinitionName,
}: {
  phase: RunPhase;
  warehouseName?: string | undefined;
  destinationDefinitionName?: string | undefined;
}): React.ReactElement {
  return (
    <span title={phaseActivityWord(phase)} className="shrink-0">
      <PhaseMark
        phase={phase.phase}
        idle={phase.activity === 'idle'}
        warehouseName={warehouseName}
        destinationDefinitionName={destinationDefinitionName}
      />
    </span>
  );
}

type LegSlot = 'phase-leg' | 'spine-in' | 'spine-out';

/**
 * Each leg piece's box and caps. `phase-leg` is a whole leg across `RunStrip`'s track, round at both
 * ends. `spine-in`/`spine-out` are the two halves of a leg on the vertical spine: a 0-wide box in the
 * rail's centred flex column (so the `border-l` rule lands under the plate), round on the leg's OUTER
 * end only, so the halves meet square and one leg reads as one rounded line. On a 2–4px border-only
 * box, `full` resolves to half the line's thickness: a round cap.
 */
const LEG_SHAPE: Record<LegSlot, string> = {
  'phase-leg': 'h-0 rounded-full',
  'spine-in': 'w-0 rounded-b-full',
  'spine-out': 'w-0 rounded-t-full',
};

/**
 * One leg of the spine — THE connector piece, in both orientations, with the same treatment for the
 * same {@link LegState} on either axis. The STATE comes from the caller's `RunPhase.legIn`/`legOut`,
 * never re-derived. Decorative: the stages' own `sr-only` activity words and the working leg's label
 * carry the state in text.
 */
export function PhaseLeg({
  leg,
  slot = 'phase-leg',
  className,
}: {
  leg: LegState;
  /** `phase-leg` on the strip's track, `spine-in`/`spine-out` for the spine's two halves. */
  slot?: LegSlot;
  className?: string;
}): React.ReactElement {
  return (
    <span
      aria-hidden
      data-slot={slot}
      data-leg={leg}
      className={cn(
        'block',
        LEG_SHAPE[slot],
        LEG_WEIGHT[slot === 'phase-leg' ? 'across' : 'down'][leg],
        LEG_TREATMENT[leg],
        className,
      )}
    />
  );
}

/**
 * The mark band: the plate (28px) with the spec's 9px of air above and below, and the stats beside it.
 * The one band a row always has, and the one that grows with the text (the live/failed row's `state`
 * line) — the mark stays centred on it, so text and mark stay level.
 */
const MARK_BAND = 'min-h-[2.875rem]';

/**
 * A leg band: `1fr` in the row's template, so a leg STRETCHES to fill whatever height the stack gives
 * the row, with a 13px floor so a leg never vanishes. The caller stacks rows in {@link PhaseStack}, whose
 * rows are all the height of the tallest, so on every run Extract's mark is at the top of the block,
 * Send's at the bottom and Plan's in the exact centre — with the 13px floor, an all-two-line settled
 * stack measures 3 x 72 = 216px, the spec's 214 within 2px, and both legs 26 + 13 = 39px.
 */
const LEG_BAND = 'min-h-[0.8125rem]';

/**
 * The row's grid template, by which leg halves it has: the mark band is `auto`, each leg band `1fr`.
 * Written out (Tailwind cannot see a class built by string concatenation).
 */
const ROW_TEMPLATE: Record<string, string> = {
  inout: 'grid-rows-[1fr_auto_1fr]',
  in: 'grid-rows-[1fr_auto]',
  out: 'grid-rows-[auto_1fr]',
  '': 'grid-rows-[auto]',
};

/**
 * The stacking container for the vertical rows: one grid column, every row the height of the tallest
 * (`auto-rows-fr`), and no gap, so the leg halves of adjacent rows touch. Equal rows are what put the
 * three marks at the top, the centre and the bottom of the block — see {@link LEG_BAND}.
 */
export function PhaseStack({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <div data-slot="phase-stack" className={cn('grid auto-rows-fr justify-items-center gap-0', className)}>
      {children}
    </div>
  );
}

/**
 * The non-visual carriers, for the two readers a shape does not reach: the `title` serves the mouse,
 * the `sr-only` span serves assistive tech.
 *
 * THESE SURVIVED THE DOTS' DELETION, and had to. State now lives on the LEGS, and a leg is a fact
 * about a segment BETWEEN two phases — it is not a per-phase verdict, so removing the dot removed the
 * only per-phase state a reader who cannot see the spine ever had. This map is that reader's whole
 * account of it. The `title` simply moved off the deleted dot onto the connector column, which is
 * `aria-hidden` and holds nothing else announced, so it behaves exactly as the dot's did.
 *
 * Neither is a VISUAL carrier, so neither weakens "run state lives on the legs" — that rule is about
 * what the plate may not do, and it still does nothing. The `sr-only` word is not redundant with the
 * row's `state` line either, and is doing MORE work than it looks: a settled phase renders no state
 * string at all (see `stateFor` in `run-phases.ts`) — on a live run and on a finished one alike — so
 * without this an unaided reading of such a row gets a number and no verdict.
 *
 * The FIFTH word is not in this map: a `void` phase (the run stopped before reaching it — see
 * `IDLE_*` below) announces "Did not run" instead of "Not started". Both are `activity: 'idle'`, and
 * the difference between them is drawn as a skeleton versus nothing at all — which is to say, drawn
 * in a way a screen reader cannot perceive. The word is where that distinction survives unaided.
 *
 * Worded to AVOID the internal `PhaseActivity` union — "settled" is this codebase's word, not a word
 * a user has ever been shown. It is NOT the platform's own vocabulary, which is what this comment
 * used to claim: `RUN_STATUS_LABEL` (`run-view.ts`) is a different map over a different union, and
 * the two diverge in ways a reader can hear. On an `unknown`-status run the stopped phase announces
 * "Failed" while `SyncStatusBadge` one card above reads "Unrecognised status"; a live run's phase says
 * "Running" where the badge says "Running..."; and a `completedWithFailures` run's phases all say
 * "Completed" where the badge says "Completed with failures".
 *
 * That divergence is in the SAFE direction and is left alone: a phase word may not be more reassuring
 * than the badge, and none of these is. Do not "reunify" the two maps by pointing this one at
 * `RUN_STATUS_LABEL` — `PhaseActivity` has an `idle` member no run status has, and `RunStatus` has
 * three settled members this row cannot tell apart. If they are ever to agree, that is a design
 * decision about the badge, not a lookup change here.
 */
const PHASE_ACTIVITY_LABEL: Record<PhaseActivity, string> = {
  idle: 'Not started',
  live: 'Running',
  settled: 'Completed',
  failed: 'Failed',
};

/**
 * The word a VOID phase is announced by, instead of `PHASE_ACTIVITY_LABEL.idle`.
 *
 * Not a member of that map because it is not a fifth `PhaseActivity` — a void phase and a pending one
 * are both `idle`, and what separates them (`idleKind`) is a second field. It is a separate WORD
 * because "Not started" is progressive, like `RunDenominated`'s "counting": it says wait, and waiting
 * resolves it. Nothing resolves a phase on a run that has already stopped. Visually the two are a
 * skeleton versus nothing at all, and neither of those reaches a screen reader, so this word is where
 * the whole distinction survives unaided.
 */
const IDLE_VOID_LABEL = 'Did not run';

/** The word a phase's state is announced and titled by — the row's `sr-only` word and the mark's `title`. */
function phaseActivityWord(phase: RunPhase): string {
  return phase.idleKind === 'void' ? IDLE_VOID_LABEL : PHASE_ACTIVITY_LABEL[phase.activity];
}

/**
 * What the WORKING leg says, in the reader's language — a marketing persona, not a data engineer.
 * Keyed by the phase that owns the leg's outgoing half, which is the upper of the two rows a leg
 * spans and therefore the one place it can be printed exactly once.
 *
 * LEG 1 SPANS EXTRACT *AND* PLAN, so its sentence has to stay true across both. "Reading from
 * Snowflake" would stop being true the moment the diff starts, and naming the warehouse at all ties
 * a two-phase segment to the party that owns only its first half. "Preparing your audience" is true
 * of the pull and of the diff, and it is the outcome the reader cares about rather than the machinery.
 *
 * Leg 2 has one owner and can afford to name it. `destinationTypeIcon(...).label` is the same
 * registry the Send mark resolves through (`PhaseMark`), so the word beside the line and the glyph on
 * it can never name two different vendors. With no definition name it degrades to "your destination"
 * rather than the registry's `prettifyDefinitionName('')` fallback, which is the bare word
 * "Destination" and reads as a placeholder that was never filled in.
 */
function workingLegLabel(phase: RunPhase, destinationDefinitionName: string | undefined): string | null {
  if (phase.legOut !== 'working') return null;
  if (phase.phase === 'extract') return 'Preparing your audience';
  if (phase.phase === 'plan') {
    const to =
      destinationDefinitionName !== undefined && destinationDefinitionName !== ''
        ? destinationTypeIcon(destinationDefinitionName).label
        : 'your destination';
    return `Sending to ${to}`;
  }
  return null;
}

interface PhaseStageProps {
  phase: RunPhase;
  /** Omits the incoming spine stub — the line starts at the first mark, not above it. */
  isFirst?: boolean;
  /** Omits the outgoing spine — the line stops at the last mark, not below it. */
  isLast?: boolean;
  /** Extract's mark (vertical only) — `RawSqlAudience.sourceDefinitionName`, resolved case-insensitively. */
  warehouseName?: string | undefined;
  /** Send's mark (vertical only) and the working leg's label — the destination's `definitionName`. */
  destinationDefinitionName?: string | undefined;
  /**
   * `vertical` (the default) is the spine row described above, for `RunOverview`'s detail column.
   * `horizontal` is the same stage's TEXT laid out for `RunStrip`'s stepper, one of three columns under
   * the strip's track of marks and legs: no spine of its own, and every line WRAPS instead of
   * truncating, because a column is about a third of the runs pane and `truncate` there would hide the
   * breakdown.
   */
  orientation?: 'vertical' | 'horizontal';
  /**
   * Horizontal only: where the stage's text BLOCK sits in its column — `RunStrip`'s stepper anchors
   * Extract's block at the column's left edge, centres Plan's under the middle node and anchors Send's
   * at the right edge. The block is only as wide as its text (`w-max`, capped at the column), and its
   * lines are always left-aligned inside it.
   */
  align?: StageAlign;
  /**
   * Horizontal only: rendered under the stage's text, inside its block — `RunStrip`'s `50 failed`
   * badge under Send's numbers. Ignored by the vertical row, which has no such slot.
   */
  aside?: React.ReactNode;
  className?: string;
}

export function PhaseStage({
  phase,
  isFirst = false,
  isLast = false,
  warehouseName,
  destinationDefinitionName,
  orientation = 'vertical',
  align = 'start',
  aside,
  className,
}: PhaseStageProps): React.ReactElement {
  const activityWord = phaseActivityWord(phase);
  const hasIn = !isFirst && phase.legIn !== null;
  const hasOut = !isLast && phase.legOut !== null;
  const legLabel = workingLegLabel(phase, destinationDefinitionName);

  if (orientation === 'horizontal') {
    return (
      <HorizontalPhaseStage
        phase={phase}
        activityWord={activityWord}
        legLabel={legLabel}
        align={align}
        aside={aside}
        className={className}
      />
    );
  }

  return (
    // No card, border, radius, or vertical padding here — the row is transparent by design, so the
    // STACKING CONTAINER (a caller concern) is what carries the card and lets the spine run edge to
    // edge across rows.
    //
    // `w-[22rem]` (352px) is the accepted, deliberately-narrow row width, and it is NOT the width of
    // the pane it ships into: `RunsPanel`'s detail column is `w-[23rem]` (368px). Reviewed and KEPT
    // rather than deleted as geometry the container already declares — but on ONE reason, not the two
    // this comment used to give. The reason that died with the provenance footer: "this row's layout
    // decisions are stated in pixels against a known width". They are not any more; the footer's
    // placement was the only such decision and it is gone. The reason that stands on its own:
    // `truncate` on the breakdown and state lines does nothing against an unbounded width, and the
    // stories — the only place the spine can be eyeballed — stack these in a `w-fit` container, which
    // would collapse to content width and never clip. Deleting the width would also silently restyle
    // `RunsPanel`, which is not a comment correction. The 1rem shortfall against the pane is a known,
    // accepted design question, NOT a bug to close by widening this to `w-[23rem]`; if it is ever
    // resolved, the fix is to hoist the width onto the stacking container, not to grow it here.
    <div
      data-slot="phase-row"
      className={cn(
        'grid w-[22rem] grid-cols-[1.75rem_minmax(0,1fr)] gap-x-3',
        ROW_TEMPLATE[`${hasIn ? 'in' : ''}${hasOut ? 'out' : ''}`],
        className,
      )}
    >
      {/* THE RAIL — one column spanning the row's every band: this row's half of the leg coming in, the
          plate, and this row's half of the leg going out, each half stretched (`flex-1`) to the row's
          edge so it meets the neighbouring row's half. Because the bands either side of the mark band
          are equal `1fr` tracks, the middle row's plate lands in the exact centre of its row; the first
          row's sits at its top and the last row's at its bottom, so across a `PhaseStack` the three
          plates sit at the top, the centre and the bottom of the block.

          PRESENCE of a half is the caller's stacking decision (`isFirst`/`isLast`); its STATE is the
          deriver's (`legIn`/`legOut`) — no fabricated default for a leg nobody described.

          `aria-hidden` with a `title`, the split `ActivationStatusDot` uses: the title is for the
          mouse, and the announcement comes from the `sr-only` spans in the stats cell, so the phase and
          its state are read as one pair. */}
      <div
        aria-hidden
        title={activityWord}
        data-slot="phase-connector"
        data-state={phase.activity}
        className="col-start-1 row-span-full flex flex-col items-center"
      >
        {hasIn && phase.legIn !== null ? <PhaseLeg leg={phase.legIn} slot="spine-in" className="flex-1" /> : null}
        {/* 9px of air above and below a 28px plate: the spec's 46px mark band. */}
        <span className="my-[0.5625rem] flex shrink-0">
          <PhaseMark
            phase={phase.phase}
            idle={phase.activity === 'idle'}
            warehouseName={warehouseName}
            destinationDefinitionName={destinationDefinitionName}
          />
        </span>
        {hasOut && phase.legOut !== null ? <PhaseLeg leg={phase.legOut} slot="spine-out" className="flex-1" /> : null}
      </div>

      {/* The leg band above the stats: empty, and the `1fr` track that lets the leg stretch. Its 13px
          floor is what keeps a leg from ever measuring 0px. */}
      {hasIn ? <div aria-hidden data-slot="leg-band" className={cn('col-start-2', LEG_BAND)} /> : null}

      {/* THE MARK BAND's text, aligned to its plate the way the plate sits in the block: the first row's
          from the top, the middle row's centred, the last row's to the bottom. */}
      <div
        data-slot="phase-stats"
        className={cn(
          'col-start-2 flex min-w-0 flex-col',
          MARK_BAND,
          !hasIn ? 'justify-start' : !hasOut ? 'justify-end' : 'justify-center',
        )}
      >
        {/* The marks are decorative and aria-hidden, so without this an unaided reading gets three
            unlabelled numbers. Phase is carried visually by the mark, by vertical order and by the
            shape of the breakdown; this is the same solution ActivationStrip uses. */}
        <span className="sr-only">{PHASE_LABEL[phase.phase]}</span>
        {/* The phase's state, in words — see `PHASE_ACTIVITY_LABEL`. The ONLY carrier of per-phase
            state left for a reader who cannot see the spine. */}
        <span className="sr-only">{activityWord}</span>

        {/* Three outcomes, not two — see this file's "AN IDLE PHASE IS ONE OF TWO THINGS". A measured
            phase gets its number; a PENDING one gets a shape holding the space that number will take;
            a VOID one gets nothing at all. The skeleton is `aria-hidden`: the `sr-only` word already
            says which of the two idle cases this is. */}
        {phase.activity !== 'idle' && (
          <RunDenominated
            variant="stat"
            n={phase.n}
            d={phase.d}
            unit={phase.unit}
            numeratorUnit={phase.numeratorUnit}
            counted={phase.counted}
          />
        )}
        {/* 104x9 at radius 4, the shape the spec reserves. `animate-pulse` survives on purpose: PENDING
            means the number is on its way. */}
        {phase.idleKind === 'pending' && <Skeleton aria-hidden className="h-[0.5625rem] w-[6.5rem] rounded" />}

        {/* 11px, with the leading pinned: an arbitrary `text-[…]` sets font-size ONLY. Empty on an idle
            phase by construction, and omitted rather than rendered as an empty line. */}
        {phase.breakdown !== '' && (
          <p className="mt-0.5 truncate font-mono text-[0.6875rem] leading-4 text-muted-foreground">
            {phase.breakdown}
          </p>
        )}

        {/* The platform's own `state`, verbatim, on the one row it actually describes — the live or
            failed phase (`stateFor`, `run-phases.ts`). OMITTED, not rendered blank, when it is empty. */}
        {phase.state !== '' && (
          <p data-slot="phase-state" className="mt-1 truncate text-xs text-muted-foreground">
            {phase.state}
          </p>
        )}
      </div>

      {/* The leg band below the stats, beside this row's half of the outgoing leg — and the working
          leg's label lives in it. The label goes on the row that owns the leg's OUTGOING half (the
          upper of the two rows a leg spans), so it is printed once per leg, beside the segment it
          describes and between the two marks the leg joins. `text-primary`, the same token the working
          leg wears, so the words and the line are visibly one statement — the non-motion, non-hue
          carrier that keeps `working` distinguishable from `failed` when the pulse is suppressed (see
          `LEG_TREATMENT`). */}
      {hasOut ? (
        <div data-slot="leg-band" className={cn('col-start-2 flex min-w-0 items-center', LEG_BAND)}>
          {legLabel !== null && (
            <p data-slot="leg-label" className="truncate text-xs leading-4 font-medium text-primary">
              {legLabel}
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

type StageAlign = 'start' | 'center' | 'end';

/**
 * Where a horizontal stage's text block sits in its column. The block is `w-max max-w-full` (as wide as
 * its text, never wider than the column) and only its MARGIN changes, so its lines stay left-aligned
 * and share one left edge in every placement. `w-max` alone is not enough once a line wraps — see
 * {@link useFitToWrappedLines}.
 */
const ALIGN_CLASSES: Record<StageAlign, string> = {
  start: '',
  center: 'mx-auto',
  end: 'ml-auto',
};

/** The right edge of everything the block visibly draws: its text, line by line, and its text-less boxes. */
function inkRight(block: HTMLElement): number {
  let right = -Infinity;
  const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const parent = node.parentElement;
    // `sr-only` text is laid out in a 1px clipped box, and draws nothing.
    if (!parent || !node.textContent?.trim() || getComputedStyle(parent).position === 'absolute') continue;
    range.selectNodeContents(node);
    for (const rect of Array.from(range.getClientRects())) right = Math.max(right, rect.right);
  }
  // Boxes that are wider than their text, or have none: the failed badge (its padding and border) and
  // the pending skeleton.
  for (const el of Array.from(block.querySelectorAll<HTMLElement>('[data-slot="badge"], [data-slot="skeleton"]'))) {
    right = Math.max(right, el.getBoundingClientRect().right);
  }
  return right;
}

/**
 * Shrinks a centred or right-anchored block to its WRAPPED text, so its visible right edge is the
 * column's (or, centred, its middle is the column's middle).
 *
 * `w-max max-w-full` sizes the block to its text on ONE line, capped at the column. When that line is
 * wider than the column — Send's `17,500 accepted of 48,000 changed · 36.5%` in a third of the runs pane
 * — the block takes the whole column and the text wraps inside it, left-aligned, so the longest line
 * that is actually drawn ends well short of the block's right edge: the destination's numbers sat about
 * 120px left of the destination mark above them. CSS has no "as wide as the wrapped lines" size, so this
 * measures the drawn lines and sets the block to the widest. Re-wrapping is impossible: every line
 * already fits that width, and every break happened because the next word did not fit a WIDER box.
 *
 * Runs after every render (the numbers of a live run change) and on every resize of the column, and
 * first clears its own width so each measurement starts from the CSS size. The width is a measured
 * value, not a design length, hence the one inline `px`. `start` blocks need none of this: their left
 * edge is what is aligned, and a ragged right is what left-aligned text has anyway.
 */
function fitToWrappedLines(block: HTMLElement | null, enabled: boolean): void {
  if (!block) return;
  block.style.width = '';
  if (!enabled || typeof document.createRange().getClientRects !== 'function') return;
  const box = block.getBoundingClientRect();
  const right = inkRight(block);
  if (!Number.isFinite(right) || box.width === 0) return;
  const width = Math.ceil(right - box.left);
  if (width < box.width - 0.5) block.style.width = `${width}px`;
}

function useFitToWrappedLines(ref: React.RefObject<HTMLElement | null>, enabled: boolean): void {
  // Every render: a live run's numbers change the lines.
  useLayoutEffect(() => fitToWrappedLines(ref.current, enabled));
  // Every resize of the column, and once the web font has loaded (it changes every line's width).
  useLayoutEffect(() => {
    const column = ref.current?.parentElement;
    if (!column || typeof ResizeObserver === 'undefined') return;
    let lastWidth = -1;
    let frame = 0;
    // The fit runs in the next frame, not inside the callback: clearing and re-setting the block's width
    // re-wraps its text, which changes the observed column's HEIGHT during delivery, and Chromium then
    // reports "ResizeObserver loop completed with undelivered notifications" (an error event on window)
    // on every resize. One frame later the layout is the same, without the error.
    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width ?? -1;
      if (width === lastWidth) return;
      lastWidth = width;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => fitToWrappedLines(ref.current, enabled));
    });
    observer.observe(column);
    void document.fonts?.ready.then(() => fitToWrappedLines(ref.current, enabled));
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [ref, enabled]);
}

/**
 * The horizontal stage: the vertical row's text — the same `sr-only` phase and activity words,
 * `RunDenominated` stat, pending skeleton, breakdown, platform `state` and working-leg label — without
 * the spine grid or a mark (`RunStrip`'s track draws the marks above it). Kept here, beside the
 * vertical row, so the two read every field of a `RunPhase` the same way and a new field cannot reach
 * one orientation only.
 *
 * Every line wraps (`break-words`), none truncates: in the strip a stage can be about 7rem wide, and
 * the breakdown is the one line that says WHERE the records went.
 */
function HorizontalPhaseStage({
  phase,
  activityWord,
  legLabel,
  align,
  aside,
  className,
}: {
  phase: RunPhase;
  activityWord: string;
  legLabel: string | null;
  align: StageAlign;
  aside: React.ReactNode;
  className: string | undefined;
}): React.ReactElement {
  const blockRef = useRef<HTMLDivElement>(null);
  useFitToWrappedLines(blockRef, align !== 'start');
  return (
    <div
      ref={blockRef}
      data-slot="phase-stage"
      data-orientation="horizontal"
      data-state={phase.activity}
      data-align={align}
      className={cn('flex w-max max-w-full min-w-0 items-start gap-2', ALIGN_CLASSES[align], className)}
    >
      {/* Every line LEFT-aligned, so a wrapped block keeps one clean left edge however the block
          itself is placed in its column (see {@link ALIGN_CLASSES}): the number and its words, the
          breakdown, the state, the working leg's label, and last the caller's `aside` (the strip's
          `N failed` badge). */}
      <div className="flex min-w-0 flex-1 flex-col items-start text-left">
        <span className="sr-only">{PHASE_LABEL[phase.phase]}</span>
        <span className="sr-only">{activityWord}</span>

        {phase.activity !== 'idle' && (
          <RunDenominated
            variant="stat"
            n={phase.n}
            d={phase.d}
            unit={phase.unit}
            numeratorUnit={phase.numeratorUnit}
            counted={phase.counted}
            // One type step down in a strip under 26rem (a phone), where a column is about 6rem and an
            // eight-figure count at `text-xl` would be wider than it.
            className="min-w-0 gap-y-0.5 @max-[26rem]:[&>span:first-child]:text-lg"
          />
        )}
        {phase.idleKind === 'pending' && <Skeleton aria-hidden className="mt-2.5 h-[0.5625rem] w-[6.5rem] rounded" />}

        {phase.breakdown !== '' && (
          // Each `label n` pair is an unbreakable inline block, and a separator stays at the end of its
          // line: a column is a third of the runs pane, and a bare `0` or a line that starts with `·`
          // reads as noise. In a strip under 26rem (a phone) a column is about 6.5rem, narrower than
          // `updated 12,800 ·` in mono, so there a pair may break between its label and its number.
          <p className="mt-0.5 font-mono text-[0.6875rem] leading-4 text-muted-foreground">
            {phase.breakdown.split(' · ').map((part, index, parts) => (
              <Fragment key={part}>
                <span className="inline-block whitespace-nowrap @max-[26rem]:whitespace-normal">
                  {part}
                  {index < parts.length - 1 ? ' ·' : null}
                </span>
                {index < parts.length - 1 ? ' ' : null}
              </Fragment>
            ))}
          </p>
        )}
        {/* The platform's `state` and the working leg's label are STAGE-level, exactly as in the
            vertical row (the state on the live/failed phase, the label on the phase that owns the
            working leg's outgoing half), so they stay in this block. Clamped to two lines; the whole
            string is the `title`. */}
        {phase.state !== '' && (
          <p
            data-slot="phase-state"
            title={phase.state}
            className="mt-1 line-clamp-2 text-xs [overflow-wrap:anywhere] text-muted-foreground"
          >
            {phase.state}
          </p>
        )}
        {legLabel !== null && (
          <p
            data-slot="leg-label"
            title={legLabel}
            className="mt-1 line-clamp-2 text-xs font-medium [overflow-wrap:anywhere] text-primary"
          >
            {legLabel}
          </p>
        )}
        {aside ? <div className="mt-1.5 flex">{aside}</div> : null}
      </div>
    </div>
  );
}
